import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Monitor, Smartphone, Tablet, X, Clock, MapPin, Wifi, CheckCircle, XCircle } from 'lucide-react';

interface DeviceInfo {
  browser: string;
  os: string;
  platform: string;
  vendor: string | null;
  model: string | null;
  deviceName: string | null;
}

interface SessionData {
  sessionId: string;
  deviceInfo: DeviceInfo;
  ipAddress: string;
  createdAt: string;
  lastActivityAt: string;
  isActive: boolean;
}

interface Props {
  userId: string;
  username: string;
  onClose: () => void;
}

const sessionColor = (count: number) => {
  if (count <= 3) return '#16a34a';
  if (count <= 5) return '#d97706';
  return '#dc2626';
};

const deviceIcon = (platform: string) => {
  if (platform === 'mobile') return <Smartphone size={18} />;
  if (platform === 'tablet') return <Tablet size={18} />;
  return <Monitor size={18} />;
};

const deviceLabel = (info: DeviceInfo): string => {
  if (info.vendor && info.model) return `${info.vendor} ${info.model}`;
  if (info.deviceName && info.deviceName !== 'Computador') return info.deviceName;
  return info.browser || 'Dispositivo desconhecido';
};

const fmtDate = (d: string) =>
  new Date(d).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

const RyanUserSessionsModal: React.FC<Props> = ({ userId, username, onClose }) => {
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const tok = localStorage.getItem('accessToken') || '';
    fetch(`/api/admin/users/${userId}/sessions/history?platform=ryan`, {
      headers: { Authorization: `Bearer ${tok}` },
    })
      .then(r => r.json())
      .then(d => {
        setSessions(d.sessions || []);
        setTotal(d.total ?? 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  const color = sessionColor(total);

  const modal = (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.55)',
        zIndex: 99999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14,
          width: '100%', maxWidth: 520, maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '20px 24px 16px',
          borderBottom: '1px solid #E5E7EB',
          position: 'sticky', top: 0, background: '#fff', zIndex: 1,
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>
              Histórico de dispositivos
            </div>
            <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>
              {username} · últimos 30 dias
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {!loading && (
              <span style={{
                padding: '4px 12px', borderRadius: 20,
                background: color, color: '#fff',
                fontSize: 12, fontWeight: 700,
              }}>
                {total} {total === 1 ? 'dispositivo' : 'dispositivos'}
              </span>
            )}
            <button
              onClick={onClose}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: '#6B7280', padding: 4, display: 'flex',
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Legend */}
        <div style={{
          display: 'flex', gap: 16, padding: '12px 24px',
          background: '#F9FAFB', borderBottom: '1px solid #E5E7EB',
          fontSize: 12, color: '#6B7280', flexWrap: 'wrap', alignItems: 'center',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Wifi size={12} color="#16a34a" /> até 3 — normal
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Wifi size={12} color="#d97706" /> 4–5 — atenção
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Wifi size={12} color="#dc2626" /> +5 — crítico
          </span>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 24px 24px' }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#6B7280', fontSize: 14 }}>
              Carregando...
            </div>
          )}
          {!loading && sessions.length === 0 && (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#9CA3AF', fontSize: 14 }}>
              Nenhum dispositivo encontrado nos últimos 30 dias
            </div>
          )}
          {!loading && sessions.map((s, i) => (
            <div
              key={s.sessionId}
              style={{
                display: 'flex', gap: 14, alignItems: 'flex-start',
                padding: '14px 0',
                borderBottom: i < sessions.length - 1 ? '1px solid #F3F4F6' : 'none',
              }}
            >
              <div style={{
                width: 40, height: 40,
                background: s.isActive ? '#F0FDF4' : '#F3F4F6',
                borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: s.isActive ? '#2F7130' : '#9CA3AF',
                flexShrink: 0,
              }}>
                {deviceIcon(s.deviceInfo.platform)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontWeight: 600, fontSize: 14, color: '#111827',
                  marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  {deviceLabel(s.deviceInfo)}
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 3,
                    fontSize: 11, fontWeight: 600,
                    color: s.isActive ? '#16a34a' : '#6B7280',
                  }}>
                    {s.isActive ? <><CheckCircle size={11} /> ativo</> : <><XCircle size={11} /> encerrado</>}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: '#6B7280', display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Monitor size={12} />
                    {s.deviceInfo.browser} · {s.deviceInfo.os}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <MapPin size={12} />
                    {s.ipAddress || 'IP não disponível'}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Clock size={12} />
                    Última atividade: {fmtDate(s.lastActivityAt)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default RyanUserSessionsModal;
